import {
  useCallback, useMemo,
} from 'react';
import { useAjaxCall } from '~/_shared/utils/hooks/useAjaxCall';
import type { DataLayerData } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.state';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import {
  createDataLayerTemplate,
  type DataLayerTemplateResponse,
  getDataLayerTemplates,
} from './dataLayers.repository';

const EMPTY_TEMPLATES: ReadonlyArray<DataLayerTemplateResponse> = [];

export const useDataLayersTemplates = () => {
  const clientId = useClientIdSelector();

  const fetchTemplates = useCallback(() => {
    if (clientId) {
      return getDataLayerTemplates(clientId);
    }
    return Promise.resolve({ data: EMPTY_TEMPLATES });
  }, [clientId]);

  const { isLoading: isFetchLoading, error: fetchError, data: fetchData, invokeAjax: fetchInvokeAjax } = useAjaxCall(fetchTemplates);
  const { isLoading: isCreateLoading, invokeAjax: create } = useAjaxCall(createDataLayerTemplate);

  const templates = useMemo(() => fetchData?.data ?? EMPTY_TEMPLATES, [fetchData]);

  const fetchDataLayerTemplates = useCallback(() => {
    return fetchInvokeAjax();
  }, [fetchInvokeAjax]);

  const createNewTemplate = useCallback((dataLayer: DataLayerData) => {
    if (!clientId) {
      return Promise.reject();
    }
    return create(clientId, dataLayer);
  }, [clientId, create]);

  return {
    templates,
    fetchDataLayerTemplates,
    createNewTemplate,
    isLoading: isFetchLoading || isCreateLoading,
    fetchError,
  };
};
